import { Link } from 'react-router-dom';
import { X, Check, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Props for the HelpMenuBlade component
 */
interface HelpMenuBladeProps {
  /** Whether the blade is open */
  isOpen: boolean;
  /** Handler for when the blade should be closed */
  onClose: () => void;
  /** The title of the help menu */
  title: string;
  /** Optional description for the help menu */
  description?: string;
  /** Whether the source is configured */
  hasSource: boolean;
  /** Whether the target is configured */
  hasTarget: boolean;
  /** Whether tables are selected */
  hasTables: boolean;
  /** Whether quiet time is configured */
  hasQuietTime: boolean;
  /** Whether delete mode is configured */
  hasDeleteMode: boolean;
  /** Whether initial sync has been triggered */
  hasTriggeredInitialSync?: boolean;
  /** Organization ID for resume setup link */
  orgId?: string;
  /** Profile ID for resume setup link */
  profileId?: string;
  /** Whether to show the resume setup button */
  showResumeSetup?: boolean;
}

/**
 * A persistent inline help menu blade that slides in from the right side
 * and pushes/squishes the main content area
 */
export function HelpMenuBlade({
  isOpen,
  onClose,
  title,
  description,
  hasSource,
  hasTarget,
  hasTables,
  hasQuietTime,
  hasDeleteMode,
  hasTriggeredInitialSync = false,
  orgId,
  profileId,
  showResumeSetup = false,
}: HelpMenuBladeProps) {
  const { t } = useLanguage();

  /**
   * Gets the URL to resume the setup wizard at the appropriate step
   */
  const getResumeWizardUrl = () => {
    if (!orgId || !profileId) return '';
    if (!hasSource) {
      return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`;
    }
    if (!hasTarget) {
      return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`;
    }
    if (!hasDeleteMode) {
      return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/delete-mode`;
    }
    // If has both source and target, go to tables step
    return `/dashboard/orgs/${orgId}/profiles/${profileId}/setup/tables`;
  };

  const checklistItems = [
    {
      label: t.profileDetail.helpContent.checklistItems.configureSource,
      completed: hasSource,
    },
    {
      label: t.profileDetail.helpContent.checklistItems.configureTarget,
      completed: hasTarget,
    },
    {
      label: t.profileDetail.helpContent.checklistItems.selectTables,
      completed: hasTables,
    },
    {
      label: t.profileDetail.helpContent.checklistItems.configureQuietTime,
      completed: hasQuietTime,
    },
    {
      label: t.profileDetail.helpContent.checklistItems.configureDeleteMode,
      completed: hasDeleteMode,
    },
    {
      label: t.profileDetail.helpContent.checklistItems.runInitialSync,
      completed: hasTriggeredInitialSync,
    },
  ];

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'bg-background shadow-lg',
          'transition-all duration-300 ease-in-out',
          'overflow-hidden flex-shrink-0',
          'flex flex-col',
          // Mobile: fixed full-screen overlay
          'fixed inset-0 z-50 md:relative md:inset-auto md:z-auto',
          // Desktop: inline with border and rounded corners
          'md:border-l md:rounded-md md:self-start',
          // Width and visibility
          isOpen ? 'md:w-96' : 'w-0',
          isOpen ? 'opacity-100 md:border' : 'opacity-0 md:border-0'
        )}
        aria-label="Help menu"
        aria-hidden={!isOpen}
      >
      {isOpen && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold truncate">{title}</h2>
              {description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{description}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 ml-2 flex-shrink-0"
              aria-label="Close help menu"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="space-y-3">
              {checklistItems.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border transition-colors',
                    item.completed ? 'background-success border-success' : 'background-neutral border-neutral'
                  )}
                >
                  <div
                    className={cn(
                      'flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center',
                      item.completed
                        ? 'background-success-strong text-success-strong'
                        : 'background-neutral-strong text-neutral-strong'
                    )}
                  >
                    {item.completed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  </div>
                  <span
                    className={cn(
                    'text-sm font-medium',
                    item.completed ? 'text-success' : 'text-neutral'
                    )}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            {hasSource && hasTarget && hasTables && hasQuietTime && hasDeleteMode && (
              <div className="mt-4 p-3 background-success border border-success rounded-lg">
                <p className="text-sm text-success font-medium">
                  {t.profileDetail.helpContent.allStepsCompleted}
                </p>
              </div>
            )}

            {/* Resume Setup Button */}
            {showResumeSetup && orgId && profileId && (
              <Link to={getResumeWizardUrl()} className="block mt-4">
                <Button variant="outline" className="w-full">
                  <Wand2 className="h-4 w-4 mr-2" />
                  {t.profileDetail.actions.resumeSetup}
                </Button>
              </Link>
            )}
          </div>
        </>
      )}
    </aside>
    </>
  );
}
